import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FilePlus2, FileSearch, Users, Boxes, ShoppingCart,
  Calculator, FileSpreadsheet, PackageSearch, Bell, BarChart3,
  Settings, ChevronDown, LogOut, ShieldCheck, BookOpen,
  CreditCard, Crown, Moon, Sun
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useIntegrationsHealth } from "../context/IntegrationsHealthContext";
import { useSubscription } from "../context/SubscriptionContext";
import CompanySwitcher from "./CompanySwitcher";
import AmbienteSwitcher from "./AmbienteSwitcher";
import FeatureLock from "./FeatureLock";
import { IS_LOCAL_EDITION } from "../lib/edition";
import { useAmbiente } from "../context/AmbienteContext";

/**
 * Sidebar profesional reorganizado según prompt maestro:
 * A. Dashboard
 * B. Emitir DTE (grupo colapsable con todos los tipos)
 * C. Documentos emitidos
 * D. Clientes
 * E. Compras / Créditos fiscales recibidos
 * F. Libros contables
 * G. IVA y anexos
 * H. Importaciones
 * I. Alertas / Mensajes del sistema
 * J. Reportes
 * K. Configuración
 */

const DTE_TYPES = [
  { tag: "01", label: "Factura", to: "/factura" },
  { tag: "03", label: "Crédito Fiscal", to: "/credito-fiscal" },
  { tag: "04", label: "Remisión", to: "/remision" },
  { tag: "05", label: "Nota Crédito", to: "/nota-credito" },
  { tag: "06", label: "Nota Débito", to: "/nota-debito" },
  { tag: "07", label: "Retención", to: "/retencion" },
  { tag: "08", label: "Liquidación", to: "/liquidacion" },
  { tag: "09", label: "Doc Contable", to: "/doc-contable" },
  { tag: "11", label: "Exportación", to: "/exportacion" },
  { tag: "14", label: "Sujeto Excluido", to: "/sujeto-excluido" },
  { tag: "15", label: "Donación", to: "/donacion" },
];

const EMITIR_OPEN_KEY = "dteflow_sidebar_emitir_open";
const EVENTOS_OPEN_KEY = "dteflow_sidebar_eventos_open";

function getInitialSidebarOpen(key, fallback) {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch (_) {}
  return fallback;
}

export default function Sidebar({
  progress = [],
  alertasCount = 0,
  theme = "light",
  onToggleTheme,
  className = "",
  onNavigate,
}) {
  const { user, logout } = useAuth();
  const { data: health } = useIntegrationsHealth();
  const { hasFeature } = useSubscription();
  const { ambiente } = useAmbiente();
  const nav = useNavigate();
  const [emitirOpen, setEmitirOpen] = useState(() => getInitialSidebarOpen(EMITIR_OPEN_KEY, !IS_LOCAL_EDITION));
  const [eventosOpen, setEventosOpen] = useState(() => getInitialSidebarOpen(EVENTOS_OPEN_KEY, false));

  useEffect(() => {
    try { window.localStorage.setItem(EMITIR_OPEN_KEY, emitirOpen ? "1" : "0"); } catch (_) {}
  }, [emitirOpen]);

  useEffect(() => {
    try { window.localStorage.setItem(EVENTOS_OPEN_KEY, eventosOpen ? "1" : "0"); } catch (_) {}
  }, [eventosOpen]);

  const handleLogout = () => {
    if (IS_LOCAL_EDITION) return;
    logout();
    nav("/login", { replace: true });
  };
  const isAdmin = ["administrador", "owner", "admin"].includes((user?.role || "").toLowerCase());
  const isSuperAdmin = !IS_LOCAL_EDITION && (user?.global_role || "").toLowerCase() === "super_admin";
  const userDisplayName = (user?.name || user?.email || "Usuario").trim();
  const userEmail = user?.email || "";
  const userInitial = userDisplayName.charAt(0).toUpperCase();

  const asideClass = `environment-sidebar w-60 shrink-0 border-r border-border bg-white h-screen sticky top-0 flex flex-col ${className}`.trim();

  return (
    <aside
      className={asideClass}
      data-testid="app-sidebar"
      data-ambiente={ambiente === "00" ? "00" : "01"}
      onClick={(e) => {
        if (!onNavigate) return;
        const el = e.target.closest("a");
        if (el) onNavigate();
      }}
    >
      {/* Marca y cambio de ambiente siempre visibles, fuera del contenido */}
      <div className="px-4 py-4 border-b border-border">
        <div className="flex items-center justify-center mb-2.5">
          <div className="relative shrink-0 flex items-center gap-2">
            <img
              src="/logo-mark-192.png"
              alt="DTE Flow"
              className="h-10 w-10 object-contain"
              data-testid="sidebar-brand-icon"
            />
            <span className="text-lg font-bold tracking-tight text-slate-900" aria-label="DTE Flow">
              DTE <span className="text-blue-600">FLOW</span>
            </span>
            <span
              className={`environment-live-dot absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white pulse-dot ${ambiente === "00" ? "is-tests" : "is-production"}`}
              title={ambiente === "00" ? "Pruebas MH activas" : "Producción activa"}
            />
          </div>
        </div>
        {!IS_LOCAL_EDITION && <CompanySwitcher />}
        {!IS_LOCAL_EDITION && <AmbienteSwitcher onNavigate={onNavigate} />}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {/* Super Admin (solo dueño de la plataforma) */}
        {isSuperAdmin && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              `sidebar-link border border-amber-200 dark:border-amber-500/60 bg-gradient-to-r from-amber-50 to-amber-100/40 dark:from-amber-500/18 dark:to-amber-400/8 text-amber-900 dark:text-amber-100 hover:from-amber-100 hover:to-amber-50 dark:hover:from-amber-500/24 dark:hover:to-amber-400/14 ${isActive ? "ring-2 ring-amber-300 dark:ring-amber-500/55" : ""}`
            }
            data-testid="nav-admin"
          >
            <Crown className="w-4 h-4 text-amber-600 dark:text-amber-300" strokeWidth={2} />
            <span className="flex-1 font-semibold">Admin Plataforma</span>
            <span className="text-[9px] tabular-nums px-1.5 py-0.5 rounded bg-amber-200 dark:bg-amber-400/20 text-amber-800 dark:text-amber-100 font-bold tracking-wider">SA</span>
          </NavLink>
        )}

        {/* A. Dashboard */}
        <NavLink to="/dashboard" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-dashboard">
          <LayoutDashboard className="w-4 h-4" strokeWidth={1.75} />
          <span>Dashboard</span>
        </NavLink>

        {/* B. Emitir DTE (grupo colapsable) */}
        <button type="button"
                onClick={() => setEmitirOpen(o => !o)}
                className="sidebar-link w-full"
                data-testid="nav-emitir-toggle">
          <FilePlus2 className="w-4 h-4" strokeWidth={1.75} />
          <span className="flex-1 text-left">Emitir DTE</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${emitirOpen ? "rotate-180" : ""}`} />
        </button>
        {emitirOpen && (
          <div className="ml-3 pl-2 border-l border-slate-100 space-y-0.5">
            {DTE_TYPES.map(t => {
              return (
                <NavLink key={t.to} to={t.to}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""} text-[12px]`}
                  data-testid={`nav-dte-${t.tag}`}>
                  <span className="flex-1 truncate">{t.label}</span>
                  <span className="font-mono text-[9px] text-slate-400 tabular-nums">{t.tag}</span>
                </NavLink>
              );
            })}
            <button type="button" onClick={() => setEventosOpen(o => !o)}
              className="sidebar-link w-full text-[12px]" data-testid="nav-eventos-toggle">
              <span className="flex-1 text-left">Eventos</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${eventosOpen ? "rotate-180" : ""}`} />
            </button>
            {eventosOpen && (
              <div className="ml-2 pl-2 border-l border-slate-50">
                <NavLink to="/invalidacion" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""} text-[11.5px]`} data-testid="nav-inv">Invalidación</NavLink>
                <NavLink to="/contingencia" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""} text-[11.5px]`} data-testid="nav-cont">Contingencia</NavLink>
                <NavLink to="/evento-retorno" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""} text-[11.5px]`} data-testid="nav-eret">Evento de Retorno</NavLink>
              </div>
            )}
          </div>
        )}

        {/* C. Documentos emitidos */}
        <NavLink to="/documentos" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-documentos">
          <FileSearch className="w-4 h-4" strokeWidth={1.75} />
          <span>Documentos emitidos</span>
        </NavLink>

        {/* D. Clientes */}
        <NavLink to="/clientes" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-clientes">
          <Users className="w-4 h-4" strokeWidth={1.75} />
          <span>Clientes</span>
        </NavLink>

        <FeatureLock feature="inventory" showLockIcon={false} className="block w-full">
          <NavLink to="/inventario" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-inventario">
            <Boxes className="w-4 h-4" strokeWidth={1.75} />
            <span>Inventario</span>
          </NavLink>
        </FeatureLock>

        {/* E. Compras (Pro) */}
        {hasFeature("purchase_book_manual") && (
          <NavLink to="/compras" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-compras">
            <ShoppingCart className="w-4 h-4" strokeWidth={1.75} />
            <span>Compras</span>
          </NavLink>
        )}

        {/* F. Libros (Pro) */}
        {hasFeature("accounting_basic") && (
          <NavLink to="/libros" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-libros">
            <Calculator className="w-4 h-4" strokeWidth={1.75} />
            <span>Libros contables</span>
          </NavLink>
        )}

        {/* G. IVA y anexos (Pro) */}
        {hasFeature("iva_auto") && (
          <NavLink to="/iva" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-iva">
            <FileSpreadsheet className="w-4 h-4" strokeWidth={1.75} />
            <span>IVA y anexos</span>
          </NavLink>
        )}

        {/* H. Importaciones */}
        <NavLink to="/importaciones" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-importaciones">
          <PackageSearch className="w-4 h-4" strokeWidth={1.75} />
          <span>Importaciones</span>
        </NavLink>

        {/* I. Alertas */}
        <NavLink to="/alertas" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-alertas">
          <Bell className="w-4 h-4" strokeWidth={1.75} />
          <span className="flex-1">Alertas</span>
          {alertasCount > 0 && (
            <span className="text-[9.5px] tabular-nums px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold" data-testid="nav-alertas-count">
              {alertasCount}
            </span>
          )}
        </NavLink>

        {/* J. Reportes */}
        <NavLink to="/reportes" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-reportes">
          <BarChart3 className="w-4 h-4" strokeWidth={1.75} />
          <span>Reportes</span>
        </NavLink>

        {/* K. Configuración */}
        <NavLink to="/configuracion" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-config">
          <Settings className="w-4 h-4" strokeWidth={1.75} />
          <span>Configuración</span>
        </NavLink>

        <NavLink to="/configuracion/guia" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-guias">
          <BookOpen className="w-4 h-4" strokeWidth={1.75} />
          <span>Guías</span>
        </NavLink>

        {/* Suscripción (iter51) */}
        {!IS_LOCAL_EDITION && (
          <NavLink to="/suscripcion" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-suscripcion">
            <CreditCard className="w-4 h-4" strokeWidth={1.75} />
            <span>Suscripción</span>
          </NavLink>
        )}

        {/* Equipo / Miembros solo para planes con gestión de roles */}
        {!IS_LOCAL_EDITION && isAdmin && hasFeature("roles") && (
          <NavLink to="/equipo" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-equipo">
            <ShieldCheck className="w-4 h-4" strokeWidth={1.75} />
            <span>Equipo</span>
          </NavLink>
        )}

        {!IS_LOCAL_EDITION && hasFeature("roles") && (
          <NavLink to="/miembros" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-miembros">
            <Users className="w-4 h-4" strokeWidth={1.75} />
            <span>Miembros</span>
          </NavLink>
        )}
      </nav>

      {/* Footer KPI */}
      <div className="px-4 py-3 border-t border-border">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="text-[11px] text-muted-foreground font-medium">
            Tema visual
          </div>
          <button
            type="button"
            onClick={onToggleTheme}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-[11px] text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            data-testid="theme-toggle"
            title={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
          >
            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            <span>{theme === "dark" ? "Claro" : "Oscuro"}</span>
          </button>
        </div>
        {!IS_LOCAL_EDITION ? (
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-300 font-medium">
              <ShieldCheck className="w-3.5 h-3.5" />
              {ambiente === "00" ? "Pruebas MH activas" : "Producción activa"}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-300 font-medium mb-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            Producción activa
          </div>
        )}
        {!IS_LOCAL_EDITION && user && (
          <div className="pt-2 border-t flex items-center gap-2" data-testid="sidebar-user">
            <div className="w-7 h-7 rounded-full bg-slate-900 text-white text-[11px] font-semibold flex items-center justify-center shrink-0">
              {userInitial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-medium truncate" data-testid="sidebar-user-name">{userDisplayName}</div>
              <div className="text-[10px] text-muted-foreground truncate" data-testid="sidebar-user-email">{userEmail}</div>
            </div>
            <button onClick={handleLogout} title="Cerrar sesión" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-300" data-testid="sidebar-logout-btn">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
